import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';
import { useMediaStore } from './media';
import { useSupplierPortalAdminStore } from './supplierPortalAdmin';
import {
	buildClientDocumentPayload,
	getClientDocumentCounterparty,
	getClientDocumentDate,
	getClientDocumentDefinition,
	getClientDocumentLabel,
	getClientDocumentReference,
	getClientDocumentSiteAddress,
	normalizeClientDocumentContent,
	resolveClientDocumentType,
} from '@/utils/clientDocumentRegistry';
import { PROPOSAL_DOCUMENT_TYPE } from '@/utils/proposalDocument';
import { QUOTATION_DOCUMENT_TYPE } from '@/utils/quotationDocument';
import { ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE } from '@/utils/assignmentConfirmationDocument';
import { applyAssignmentTaskTemplatesToNewContent } from '@/utils/assignmentSubtitlePresets';
import {
	createAttachmentFromMedia,
	ensureAttachmentArray,
} from '@/utils/attachments';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const DEFAULT_PAGE_SIZE = 100;

const resolveDocumentType = (attributes = {}) =>
	resolveClientDocumentType({
		documentType: attributes.documentType,
		content: attributes.content,
	});

const applyNewDocumentPresets = (content, documentType, documentPresets) =>
	resolveClientDocumentType({ documentType, content }) === ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE
		? applyAssignmentTaskTemplatesToNewContent(content, documentPresets)
		: content;

const normalizeRelation = (payload) => {
	if (!payload) return null;
	const data = payload.data || payload;
	if (!data) return null;
	const attributes = data.attributes || {};
	return {
		id: data.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? data.documentId ?? null,
		name: attributes.name ?? data.name ?? '',
	};
};

const normalizeSupportingDocuments = (payload) =>
	ensureAttachmentArray(payload?.data ?? payload ?? []);

const normalizeDocument = (entry = {}) => {
	const attributes = entry?.attributes || entry || {};
	const documentType = resolveDocumentType(attributes);
	const sourceContent = attributes.content || {};
	const content = normalizeClientDocumentContent(sourceContent, documentType);
	return {
		id: entry?.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		documentType,
		title: attributes.title || content.title || getClientDocumentLabel(documentType),
		reference: attributes.reference || getClientDocumentReference({ documentType, content }) || '',
		documentDate: attributes.documentDate || getClientDocumentDate({ documentType, content }) || null,
		client: normalizeRelation(attributes.client || entry?.client),
		clientName: attributes.clientName || getClientDocumentCounterparty({ documentType, content }) || '',
		propertySummary: attributes.propertySummary || '',
		siteAddress: getClientDocumentSiteAddress({
			documentType,
			content: sourceContent,
			metadata: attributes.metadata,
			propertySummary: attributes.propertySummary,
		}),
		totalAmount: attributes.totalAmount ?? null,
		supportingDocuments: normalizeSupportingDocuments(attributes.supportingDocuments),
		content,
		metadata: attributes.metadata || {},
		// Which saved version this is; a save names it so it cannot overwrite newer changes. A
		// server without revisions sends none, and then saves do not name one either.
		revision: Object.hasOwn(attributes, 'revision') ? Number(attributes.revision) || 0 : undefined,
		createdAt: attributes.createdAt ?? entry?.createdAt ?? null,
		updatedAt: attributes.updatedAt ?? entry?.updatedAt ?? null,
		raw: entry,
	};
};

const resolveDocumentKey = (document) => {
	const key = document?.documentId ?? document?.id;
	if (key === null || key === undefined || key === '') return '';
	return String(key);
};

const matchesDocumentIdentifier = (document, identifier) => {
	const key = String(identifier);
	return [document?.documentId, document?.id]
		.filter((value) => value !== null && value !== undefined && value !== '')
		.some((value) => String(value) === key);
};

const parseSortTimestamp = (value) => {
	const timestamp = value ? Date.parse(value) : Number.NaN;
	return Number.isFinite(timestamp) ? timestamp : 0;
};

const parseNumericId = (value) => {
	const id = Number(value);
	return Number.isFinite(id) ? id : null;
};

const compareDocumentsByCreationDesc = (a = {}, b = {}) => {
	const createdDifference = parseSortTimestamp(b.createdAt) - parseSortTimestamp(a.createdAt);
	if (createdDifference !== 0) return createdDifference;

	const aId = parseNumericId(a.id);
	const bId = parseNumericId(b.id);
	if (aId !== null && bId !== null && aId !== bId) return bId - aId;

	return resolveDocumentKey(b).localeCompare(resolveDocumentKey(a), undefined, {
		numeric: true,
		sensitivity: 'base',
	});
};

const sortDocumentsByCreationDesc = (items = []) =>
	[...items].sort(compareDocumentsByCreationDesc);

const upsertDocument = (items = [], document = null) => {
	const key = resolveDocumentKey(document);
	if (!key) return items;
	const next = [...items];
	const index = next.findIndex((item) => resolveDocumentKey(item) === key);
	if (index === -1) {
		next.unshift(document);
		return document.createdAt ? sortDocumentsByCreationDesc(next) : next;
	}
	next[index] = {
		...next[index],
		...document,
		client: document.client || next[index].client,
	};
	return next[index].createdAt ? sortDocumentsByCreationDesc(next) : next;
};

const sanitizePayload = (payload = {}) => {
	const allowed = [
		'documentType',
		'title',
		'reference',
		'documentDate',
		'client',
		'clientName',
		'propertySummary',
		'totalAmount',
		'content',
		'metadata',
		'supportingDocuments',
		'revision',
	];
	return allowed.reduce((acc, key) => {
		if (payload[key] !== undefined) {
			acc[key] = payload[key];
		}
		return acc;
	}, {});
};

const cleanupMediaFiles = async (fileIds = [], mediaStore) => {
	const ids = [...new Set((fileIds || []).filter(Boolean))];
	for (const id of ids) {
		try {
			await mediaStore.deleteFile(id);
		} catch (error) {
			console.error(`Failed to cleanup client document media file ${id}`, error);
		}
	}
};

const persistSupportingDocuments = async (attachmentsInput, mediaStore) => {
	const attachments = ensureAttachmentArray(attachmentsInput);
	const persisted = [];
	const deferredDeleteIds = [];
	const uploadedIds = [];

	try {
		for (const attachment of attachments) {
			if (!attachment) continue;
			if (attachment.status === 'delete') {
				if (attachment.id) deferredDeleteIds.push(attachment.id);
				continue;
			}
			if (attachment.status === 'new' && attachment.file) {
				const uploaded = await mediaStore.uploadFile(attachment.file);
				if (uploaded?.id) {
					uploadedIds.push(uploaded.id);
					persisted.push(createAttachmentFromMedia(uploaded));
				}
				continue;
			}
			if (attachment.id) persisted.push(attachment);
		}
	} catch (error) {
		await cleanupMediaFiles(uploadedIds, mediaStore);
		throw error;
	}

	return {
		ids: [...new Set(persisted.map((attachment) => attachment.id).filter(Boolean))],
		attachments: persisted,
		deferredDeleteIds: [...new Set(deferredDeleteIds)],
		uploadedIds: [...new Set(uploadedIds)],
	};
};

export const useClientDocumentsStore = defineStore('clientDocuments', {
	state: () => ({
		items: [],
		isLoading: false,
		isRefreshing: false,
		error: null,
		hasFetched: false,
		pagination: {
			page: 1,
			pageSize: DEFAULT_PAGE_SIZE,
			total: 0,
			pageCount: 1,
		},
		currentDocumentCache: {},
	}),
	getters: {
		documentList: (state) => state.items,
	},
	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},

		reset() {
			this.items = [];
			this.isLoading = false;
			this.isRefreshing = false;
			this.error = null;
			this.hasFetched = false;
			this.pagination = {
				page: 1,
				pageSize: DEFAULT_PAGE_SIZE,
				total: 0,
				pageCount: 1,
			};
			this.currentDocumentCache = {};
		},

		basePopulateParams() {
			return {
				'populate[client][fields][0]': 'name',
				'populate[client][fields][1]': 'documentId',
				'populate[supportingDocuments][fields][0]': 'name',
				'populate[supportingDocuments][fields][1]': 'mime',
				'populate[supportingDocuments][fields][2]': 'size',
				'populate[supportingDocuments][fields][3]': 'hash',
				'populate[supportingDocuments][fields][4]': 'ext',
				'populate[supportingDocuments][fields][5]': 'url',
			};
		},

		buildQueryParams({ page = this.pagination.page, pageSize = this.pagination.pageSize } = {}) {
			return {
				'pagination[page]': page || 1,
				'pagination[pageSize]': pageSize || DEFAULT_PAGE_SIZE,
				'sort[0]': 'createdAt:desc',
				'sort[1]': 'id:desc',
				...this.basePopulateParams(),
			};
		},

		// Reads the documents without touching the list, so a page can decide whether it is safe
		// to show what changed elsewhere.
		async peekDocuments({ page = 1, pageSize = DEFAULT_PAGE_SIZE } = {}) {
			const response = await axios.get(`${STRAPI_URL}/api/client-documents`, {
				headers: this.authHeaders(),
				params: this.buildQueryParams({ page, pageSize }),
			});
			const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
			const items = sortDocumentsByCreationDesc(rows.map(normalizeDocument));
			const meta = response?.data?.meta?.pagination || {};
			return {
				items,
				pagination: {
					page: meta.page || page,
					pageSize: meta.pageSize || pageSize,
					total: meta.total || items.length,
					pageCount: meta.pageCount || 1,
				},
			};
		},

		// Every saved document, page by page, without touching the list.
		async peekAllDocuments({ pageSize = DEFAULT_PAGE_SIZE, maxPages = 20 } = {}) {
			const first = await this.peekDocuments({ page: 1, pageSize });
			const items = [...first.items];
			const pageCount = Math.min(first.pagination.pageCount || 1, maxPages);
			for (let page = 2; page <= pageCount; page += 1) {
				const next = await this.peekDocuments({ page, pageSize });
				items.push(...next.items);
			}
			return sortDocumentsByCreationDesc(items);
		},

		applyDocuments(items, pagination) {
			this.items = items;
			this.pagination = pagination;
			this.hasFetched = true;
			items.forEach((item) => {
				const key = resolveDocumentKey(item);
				if (key) this.currentDocumentCache[key] = item;
			});
		},

		async fetchDocuments({ page = 1, pageSize = DEFAULT_PAGE_SIZE, force = false } = {}) {
			if (this.isLoading && !force) return this.items;
			const initialLoad = !this.hasFetched;
			this.isLoading = initialLoad;
			this.isRefreshing = !initialLoad;
			this.error = null;
			try {
				const { items, pagination } = await this.peekDocuments({ page, pageSize });
				this.applyDocuments(items, pagination);
				return items;
			} catch (error) {
				this.error =
					error?.response?.data?.error?.message ||
					error?.message ||
					'Failed to load client documents';
				throw error;
			} finally {
				this.isLoading = false;
				this.isRefreshing = false;
			}
		},

		// Reads a proposal written in Word on the server and returns its fields with notes on what
		// to check: { fields, warnings, fileName }. Nothing is saved.
		async readProposalFile(file) {
			const form = new FormData();
			form.append('file', file, file?.name || 'proposal.docx');
			const response = await axios.post(`${STRAPI_URL}/api/client-documents/read-proposal`, form, {
				headers: this.authHeaders(),
				// Reading a whole proposal can take a minute or two.
				timeout: 300000,
			});
			return response?.data?.data || null;
		},

		// Checks the document's spelling, grammar and wording on the server; it answers with
		// suggestions only and changes nothing.
		async proofreadDocument({ content, documentType = '' } = {}) {
			const response = await axios.post(
				`${STRAPI_URL}/api/client-documents/proofread`,
				{ content, documentType },
				// The check reads at high effort, which can take a minute or two on a long proposal.
				{ headers: this.authHeaders(), timeout: 180000 },
			);
			return response?.data?.data || { findings: [], fieldsChecked: 0 };
		},

		async fetchDocument(documentId, { forceRefresh = false } = {}) {
			if (!documentId) return null;
			const key = String(documentId);
			if (!forceRefresh && this.currentDocumentCache[key]) {
				return this.currentDocumentCache[key];
			}
			const response = await axios.get(`${STRAPI_URL}/api/client-documents/${encodeURIComponent(key)}`, {
				headers: this.authHeaders(),
				params: this.basePopulateParams(),
			});
			const document = normalizeDocument(response?.data?.data || {});
			const resolvedKey = resolveDocumentKey(document) || key;
			this.currentDocumentCache[resolvedKey] = document;
			this.items = upsertDocument(this.items, document);
			return document;
		},

		async createDocument({
			content,
			metadata = {},
			client = null,
			documentType = PROPOSAL_DOCUMENT_TYPE,
			documentPresets,
		} = {}) {
			const definition = getClientDocumentDefinition(content?.documentType || documentType);
			const initialContent = content || definition.createContent();
			const creationContent = applyNewDocumentPresets(
				initialContent,
				definition.type,
				documentPresets,
			);
			const payload = definition.buildPayload(creationContent, {
				client,
				metadata,
				documentType,
			});
			const response = await axios.post(
				`${STRAPI_URL}/api/client-documents`,
				{ data: sanitizePayload(payload) },
				{
					headers: this.authHeaders(),
					params: this.basePopulateParams(),
				},
			);
			const document = normalizeDocument(response?.data?.data || {});
			const key = resolveDocumentKey(document);
			if (key) this.currentDocumentCache[key] = document;
			this.items = upsertDocument(this.items, document);
			this.hasFetched = true;
			return document;
		},

		async createProposalDocument({ content, metadata = {}, client = null } = {}) {
			return this.createDocument({
				content,
				metadata,
				client,
				documentType: PROPOSAL_DOCUMENT_TYPE,
			});
		},

		async createQuotationDocument({ content, metadata = {}, client = null } = {}) {
			return this.createDocument({
				content,
				metadata,
				client,
				documentType: QUOTATION_DOCUMENT_TYPE,
			});
		},

		async createAssignmentConfirmationDocument({
			content,
			metadata = {},
			client = null,
			documentPresets,
		} = {}) {
			return this.createDocument({
				content,
				metadata,
				client,
				documentType: ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
				documentPresets,
			});
		},

		async saveDocument({ documentId, payload, documentPresets } = {}) {
			if (!payload) return null;
			const hasSupportingDocuments = Object.prototype.hasOwnProperty.call(
				payload,
				'supportingDocuments',
			);
			const mediaStore = useMediaStore();
			let persistedMedia = {
				ids: [],
				attachments: [],
				deferredDeleteIds: [],
				uploadedIds: [],
			};
			if (hasSupportingDocuments) {
				persistedMedia = await persistSupportingDocuments(
					payload.supportingDocuments,
					mediaStore,
				);
			}
			const targetId = documentId ? String(documentId) : '';
			const content = targetId
				? payload.content || {}
				: applyNewDocumentPresets(
						payload.content || {},
						payload.documentType,
						documentPresets,
					);
			const normalizedPayload = buildClientDocumentPayload(content, {
				...payload,
				metadata: payload.metadata || {},
				...(hasSupportingDocuments
					? { supportingDocuments: persistedMedia.ids }
					: {}),
			});
			const data = sanitizePayload(normalizedPayload);
			let response;
			try {
				response = targetId
					? await axios.put(
							`${STRAPI_URL}/api/client-documents/${encodeURIComponent(targetId)}`,
							{ data },
							{
								headers: this.authHeaders(),
								params: this.basePopulateParams(),
							},
						)
					: await axios.post(
							`${STRAPI_URL}/api/client-documents`,
							{ data },
							{
								headers: this.authHeaders(),
								params: this.basePopulateParams(),
							},
						);
			} catch (error) {
				await cleanupMediaFiles(persistedMedia.uploadedIds, mediaStore);
				throw error;
			}

			const document = normalizeDocument(response?.data?.data || {});
			if (
				hasSupportingDocuments &&
				!document.supportingDocuments.length &&
				persistedMedia.attachments.length
			) {
				document.supportingDocuments = persistedMedia.attachments;
			}
			const key = resolveDocumentKey(document) || targetId;
			if (key) this.currentDocumentCache[key] = document;
			this.items = upsertDocument(this.items, document);
			this.hasFetched = true;
			await cleanupMediaFiles(persistedMedia.deferredDeleteIds, mediaStore);
			return document;
		},

		async saveSupportingDocuments({ documentId, supportingDocuments = [] } = {}) {
			if (!documentId) {
				throw new Error('Document id is required to save supporting documents');
			}
			const key = String(documentId);
			const existingDocument =
				this.currentDocumentCache[key] ||
				this.items.find((document) => matchesDocumentIdentifier(document, key)) ||
				null;
			const mediaStore = useMediaStore();
			const persistedMedia = await persistSupportingDocuments(
				supportingDocuments,
				mediaStore,
			);

			let response;
			try {
				response = await axios.put(
					`${STRAPI_URL}/api/client-documents/${encodeURIComponent(key)}`,
					{ data: { supportingDocuments: persistedMedia.ids } },
					{
						headers: this.authHeaders(),
						params: this.basePopulateParams(),
					},
				);
			} catch (error) {
				await cleanupMediaFiles(persistedMedia.uploadedIds, mediaStore);
				throw error;
			}

			const responseEntry = response?.data?.data || {};
			const responseAttributes = responseEntry?.attributes || responseEntry || {};
			const normalizedResponse = normalizeDocument(responseEntry);
			const responseHasContent = Object.prototype.hasOwnProperty.call(
				responseAttributes,
				'content',
			);
			const responseHasSupportingDocuments = Object.prototype.hasOwnProperty.call(
				responseAttributes,
				'supportingDocuments',
			);
			const nextSupportingDocuments = responseHasSupportingDocuments
				? normalizedResponse.supportingDocuments
				: persistedMedia.attachments;
			const document = existingDocument && !responseHasContent
				? {
						...existingDocument,
						id: normalizedResponse.id || existingDocument.id,
						documentId:
							normalizedResponse.documentId || existingDocument.documentId,
						supportingDocuments: nextSupportingDocuments,
						updatedAt: normalizedResponse.updatedAt || existingDocument.updatedAt,
						raw: responseEntry,
					}
				: {
						...normalizedResponse,
						supportingDocuments: nextSupportingDocuments,
					};

			const resolvedKey = resolveDocumentKey(document) || key;
			this.currentDocumentCache[resolvedKey] = document;
			this.items = upsertDocument(this.items, document);
			this.hasFetched = true;
			await cleanupMediaFiles(persistedMedia.deferredDeleteIds, mediaStore);
			return document;
		},

		async deleteDocument(documentId) {
			if (!documentId) return;
			const key = String(documentId);
			const existingDocument =
				this.currentDocumentCache[key] ||
				this.items.find((document) => matchesDocumentIdentifier(document, key));
			const attachmentIds = ensureAttachmentArray(
				existingDocument?.supportingDocuments,
			)
				.map((attachment) => attachment.id)
				.filter(Boolean);
			await axios.delete(`${STRAPI_URL}/api/client-documents/${encodeURIComponent(key)}`, {
				headers: this.authHeaders(),
			});
			this.removeDocumentFromState(key);
			await cleanupMediaFiles(attachmentIds, useMediaStore());
		},

		removeDocumentFromState(documentId) {
			if (documentId === null || documentId === undefined || documentId === '') return false;
			const key = String(documentId);
			const previousLength = this.items.length;
			this.items = this.items.filter((item) => !matchesDocumentIdentifier(item, key));
			const removedCount = previousLength - this.items.length;

			this.currentDocumentCache = Object.fromEntries(
				Object.entries(this.currentDocumentCache).filter(
					([cacheKey, document]) =>
						String(cacheKey) !== key && !matchesDocumentIdentifier(document, key),
				),
			);

			if (removedCount > 0) {
				this.pagination.total = Math.max(0, Number(this.pagination.total || 0) - removedCount);
				const pageSize = Math.max(1, Number(this.pagination.pageSize || DEFAULT_PAGE_SIZE));
				this.pagination.pageCount = Math.max(1, Math.ceil(this.pagination.total / pageSize));
			}
			return removedCount > 0;
		},

		async transferToSupplierDocuments(documentId) {
			if (documentId === null || documentId === undefined || documentId === '') {
				throw new Error('documentId is required');
			}
			const key = String(documentId);
			const supplierPortalAdminStore = useSupplierPortalAdminStore();
			const result = await supplierPortalAdminStore.transferAssignmentFromClient(key);
			if (!result?.ok || result?.sourceRemoved !== true) {
				throw new Error('The transfer completed without confirming removal of the client document');
			}
			this.removeDocumentFromState(key);
			return result;
		},
	},
});
